// Questions management section - handles CRUD operations for questions
import React, { useState, useMemo, useRef } from "react";
import { useToast } from "../providers/ToastProvider";
import { useEditing } from "../lib/hooks/useEditing";
import { useFilter } from "../lib/hooks/useFilter";
import { useDataSource } from "../lib/hooks/useDataSource";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/Card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/Select";
import { Button } from "../components/ui/Button";
import EditorModal from "../components/ui/EditorModal";
import Confirm from "../components/ui/Confirm";
import { Input, Label } from "../components/ui/Input";
import { Textarea } from "../components/ui/Textarea";
import SearchableSelect from "../components/ui/SearchableSelect";
import { Checkbox } from "../components/ui/Checkbox";
import TruncatedText from "../components/ui/TruncatedText";
import ResponsiveTable from "../components/ui/ResponsiveTable";
import { useRestAuth } from "../providers/RestAuthProvider";
import { useLocalization } from "../providers/LocalizationProvider";
import UserSelect from "../components/ui/UserSelect";
import { USER_ROLES } from "../lib/config/constants";
import { logger } from "../lib/utils/logger";

export default function QuestionsSection({ dataSource, showTrash }) {
  // Get hooks for notifications, authentication, and localization
  const { notify } = useToast();
  const { user } = useRestAuth();
  const { t, language, userLanguageDetermined } = useLocalization();

  // Use data source hook with current language for question operations
  const {
    data: questions,
    isLoading,
    error,
    create,
    update,
    softDelete,
    restore,
    hardDelete,
    refresh,
    clearError
  } = useDataSource(dataSource, 'questions', language, userLanguageDetermined);

  // Check if current user is admin
  const isAdmin = user?.role === USER_ROLES.ADMIN;

  // Local state
  const filter = useFilter("");
  const { editing, setEditing, openNew, openEdit, close } = useEditing({
    themeId: "",
    enonce: "",
    public: false,
    userId: user?.id
  });

  // Local state for theme selection
  const [selectedThemeId, setSelectedThemeId] = useState("");
  const [confirm, setConfirm] = useState({ open: false, title: "", description: "", withChildren: false });

  // Reset selectedThemeId when editing changes
  React.useEffect(() => {
    if (editing) {
      if (editing.themeId) {
        setSelectedThemeId(editing.themeId);
      } else {
        // Try to set default theme if new
        const defaultTheme = (dataSource.state.themes || []).find(t => !t.deletedAt)?.id;
        setSelectedThemeId(defaultTheme || "");
      }
    } else {
      setSelectedThemeId("");
    }
  }, [editing, dataSource.state.themes]);
  const cascadeRef = useRef(false);

  // Ensure questions are loaded on component mount when language is determined
  React.useEffect(() => {
    if (questions.length === 0 && !isLoading && userLanguageDetermined) {
      refresh(true);
    }
  }, [questions.length, isLoading, userLanguageDetermined, refresh]);

  // Computed values
  const filteredData = useMemo(() =>
    questions.filter(q =>
      (showTrash ? q.deletedAt : !q.deletedAt) &&
      q.enonce.toLowerCase().includes(filter.value.toLowerCase())
    ),
    [questions, showTrash, filter.value]
  );

  const onSave = async (e) => {
    e.preventDefault();
    clearError();

    const f = new FormData(e.currentTarget);
    const themeId = f.get('themeId');
    const userId = f.get('userId') + "";
    const contentLanguageCode = f.get('contentLanguage') + "";

    try {
      if (editing?.id) {
        // PATCH only updates translation text, userId is optional
        const updatePayload = {
          questionText: f.get('enonce') + ""
        };
        if (userId) {
          updatePayload.userId = userId;
        }
        // Also update themeId if it changed
        if (themeId && themeId !== editing.themeId) {
          updatePayload.themeId = themeId;
        }
        await update(editing.id, updatePayload);
        notify({ type: 'success', message: t('questions.questionUpdated') });
      } else {
        // CREATE needs themeId
        const createPayload = {
          themeId: themeId || selectedThemeId || dataSource.state.themes.find(t => !t.deletedAt)?.id || "",
          questionText: f.get('enonce') + "",
          contentLanguageCode,
        };
        await create(createPayload);
        notify({ type: 'success', message: t('questions.questionCreated') });
      }
      setEditing(null);
      await refresh(true); // Force refresh to get latest data
    } catch (error) {
      // Error is already handled by the hook
      logger.error('Save error', error);
    }
  };

  const askDelete = (q) => {
    const rCount = (dataSource.state.responses || []).filter(r => !r.deletedAt && r.questionId === q.id).length;
    cascadeRef.current = false;
    setConfirm({
      open: true,
      title: t('questions.deleteConfirm'),
      description: rCount > 0 ? t('questions.deleteWithResponses', { count: rCount }) : "",
      withChildren: false,
      onConfirm: async () => {
        try {
          await softDelete(q.id);
          if (cascadeRef.current) {
            await Promise.all((dataSource.state.responses || []).filter(r => !r.deletedAt && r.questionId === q.id).map(r => dataSource.api.softDeleteResponse(r.id)));
          }
          notify({ type: 'success', message: t('questions.questionMovedToTrash') });
          setConfirm({ open: false, title: "", description: "", withChildren: false });
          await refresh(true);
        } catch (error) {
          logger.error('Delete error', error);
        }
      },
      onCancel: () => setConfirm({ open: false, title: "", description: "", withChildren: false })
    });
  };

  // Note: Themes and users data are now loaded centrally by AdminShell
  // No need for manual loading here

  // Memoize user names to ensure they update when users are loaded
  const userNames = React.useMemo(() => {
    const names = {};
    if (isAdmin && dataSource.state.users && dataSource.state.users.length > 0) {
      dataSource.state.users.forEach(user => {
        const firstName = user.firstName || '';
        const lastName = user.lastName || user.name || '';
        names[user.id] = [firstName, lastName].filter(Boolean).join(' ') || "—";
      });
    }
    return names;
  }, [isAdmin, dataSource.state.users]);

  const userName = (uid) => {
    if (!uid) return t('common.notAssigned');
    if (!isAdmin) return t('common.notAssigned');

    // If users are not loaded yet, show loading
    if (!dataSource.state.users || dataSource.state.users.length === 0) {
      return t('common.loadingText');
    }

    // If users are loaded but this specific user is not found, show unknown
    return userNames[uid] || t('common.unknown');
  };

  const canManage = (ownerId) => isAdmin || ownerId === user?.id;

  const responseCount = (qid) => (dataSource.state.responses || []).filter(r => r.questionId === qid && !r.deletedAt).length;

  const confirmExtra = confirm.description ? (
    <div className="mt-2">
      <div className="flex items-center space-x-2">
        <Checkbox
          id="deleteResponses"
          checked={!!confirm.withChildren}
          onCheckedChange={(v) => {
            cascadeRef.current = v;
            setConfirm((c) => ({ ...c, withChildren: v }));
          }}
        />
        <Label htmlFor="deleteResponses">
          {t('questions.deleteResponsesCheckbox')}
        </Label>
      </div>
    </div>
  ) : null;

  // Render loading state
  if (isLoading && questions.length === 0) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{t('questions.title')}</CardTitle>
            <div className="flex items-center gap-2">
              {filter.input}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-900"></div>
              <span className="text-slate-500">{t('questions.loadingQuestions')}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Render error state
  if (error && questions.length === 0) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{t('questions.title')}</CardTitle>
            <div className="flex items-center gap-2">
              {filter.input}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 space-y-4">
            <div className="text-red-600 text-center">
              <p className="font-medium">{t('questions.loadingError')}</p>
              <p className="text-sm">{error}</p>
            </div>
            <Button
              onClick={() => refresh(true)}
            >
              {t('questions.retry')}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="space-y-4">
          <CardTitle>{t('questions.title')}</CardTitle>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-end">
            {!showTrash && user.role === USER_ROLES.ADMIN && (
              <Button
                onClick={openNew}
                disabled={isLoading}
                className="w-full sm:w-auto"
              >
                {t('questions.newQuestion')}
              </Button>
            )}
            <div className="w-full sm:w-auto">
              {filter.input}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Error banner */}
        {error && (
          <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="text-destructive text-sm">{error}</span>
              <Button
                onClick={clearError}
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive"
              >
                ×
              </Button>
            </div>
          </div>
        )}

        <ResponsiveTable
          data={filteredData}
          columns={[
            { header: t('questions.statement'), className: "max-w-xs" },
            { header: t('questions.theme'), className: "max-w-xs" },
            { header: t('common.public') },
            { header: t('questions.responses') },
            ...(isAdmin ? [{ header: t('common.linkedUser') }] : []),
            { header: t('common.actions'), className: "w-56 text-right" }
          ]}
          renderRow={(q) => (
            <tr key={q.id} className="border-b hover:bg-muted/50">
              <td className="py-2 max-w-xs">
                <TruncatedText text={q.enonce} maxLength={60} />
              </td>
              <td className="py-2 max-w-xs">
                <TruncatedText
                  text={q.themeId ?
                    (dataSource.state.themes || []).find(t => t.id === q.themeId)?.nom || t('questions.themeUnknown')
                    : t('questions.themeNotAssigned')
                  }
                  maxLength={40}
                />
              </td>
              <td className="py-2">{q.public ? t('common.yes') : t('common.no')}</td>
              <td className="py-2">{responseCount(q.id)}</td>
              {isAdmin && <td className="py-2">{userName(q.userId)}</td>}
              <td className="py-2 text-right space-x-2">
                {!showTrash ? <>
                  {canManage(q.userId) && <Button onClick={() => openEdit(q)} variant="outline" size="sm" disabled={isLoading}>{t('common.edit')}</Button>}
                  {canManage(q.userId) && <Button onClick={() => askDelete(q)} variant="outline" size="sm" className="text-red-600" disabled={isLoading}>{t('common.delete')}</Button>}
                </> : <>
                  {canManage(q.userId) && <Button onClick={async () => { await restore(q.id); notify({ type: 'success', message: t('questions.questionRestored') }); await refresh(true); }} variant="outline" size="sm" disabled={isLoading}>{t('common.restore')}</Button>}
                  {canManage(q.userId) && <Button onClick={async () => { await hardDelete(q.id); notify({ type: 'success', message: t('questions.questionDeletedPermanent') }); await refresh(true); }} variant="outline" size="sm" className="text-red-600" disabled={isLoading}>{t('common.deletePermanent')}</Button>}
                </>}
              </td>
            </tr>
          )}
          renderMobileCard={(q) => (
            <div className="space-y-3 w-full max-w-full overflow-hidden">
              <div className="w-full max-w-full overflow-hidden">
                <div className="text-sm font-medium text-muted-foreground mb-1">{t('questions.statement')}</div>
                <div className="text-sm break-words overflow-wrap-anywhere">{q.enonce}</div>
              </div>
              <div className="w-full max-w-full overflow-hidden">
                <div className="text-sm font-medium text-muted-foreground mb-1">{t('questions.theme')}</div>
                <div className="text-sm break-words overflow-wrap-anywhere">
                  {q.themeId ?
                    (dataSource.state.themes || []).find(t => t.id === q.themeId)?.nom || t('questions.themeUnknown')
                    : t('questions.themeNotAssigned')
                  }
                </div>
              </div>
              <div className="flex justify-between items-center w-full max-w-full">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-muted-foreground mb-1">{t('common.public')}</div>
                  <div className="text-sm">{q.public ? t('common.yes') : t('common.no')}</div>
                </div>
                <div className="min-w-0 flex-1 ml-2">
                  <div className="text-sm font-medium text-muted-foreground mb-1">{t('questions.responses')}</div>
                  <div className="text-sm">{responseCount(q.id)}</div>
                </div>
              </div>
              {isAdmin && (
                <div className="w-full max-w-full overflow-hidden">
                  <div className="text-sm font-medium text-muted-foreground mb-1">{t('common.linkedUser')}</div>
                  <div className="text-sm break-words">{userName(q.userId)}</div>
                </div>
              )}
              {canManage(q.userId) && (
                <div className="flex gap-2 pt-2 w-full max-w-full">
                  {!showTrash ? (
                    <>
                      <Button onClick={() => openEdit(q)} variant="outline" size="sm" disabled={isLoading} className="flex-1 min-w-0">
                        {t('common.edit')}
                      </Button>
                      <Button onClick={() => askDelete(q)} variant="outline" size="sm" className="flex-1 min-w-0 text-red-600" disabled={isLoading}>
                        {t('common.delete')}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button onClick={async () => { await restore(q.id); notify({ type: 'success', message: t('questions.questionRestored') }); await refresh(true); }} variant="outline" size="sm" disabled={isLoading} className="flex-1 min-w-0">
                        {t('common.restore')}
                      </Button>
                      <Button onClick={async () => { await hardDelete(q.id); notify({ type: 'success', message: t('questions.questionDeletedPermanent') }); await refresh(true); }} variant="outline" size="sm" className="flex-1 min-w-0 text-red-600" disabled={isLoading}>
                        {t('common.deletePermanent')}
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
          emptyMessage={isLoading ? t('common.loading') : t('common.noItems')}
        />
      </CardContent>

      <EditorModal open={!!editing} title={editing?.id ? t('questions.editQuestion') : t('questions.newQuestion')} onClose={close} onSubmit={onSave}>
        {isAdmin ? (
          <UserSelect name="userId" label={t('questions.linkedUser')} users={dataSource.state.users || []} defaultValue={editing?.userId || user?.id} />
        ) : (
          <>
            <div className="space-y-2">
              <Label htmlFor="linkedUser">{t('questions.linkedUser')}</Label>
              <Input id="linkedUser" value={user?.name || ""} readOnly disabled />
            </div>
            <input type="hidden" name="userId" value={user?.id || ""} />
          </>
        )}
        <div className="w-full min-w-0 max-w-full">
          {(() => {
            const defaultValue = editing?.themeId || (dataSource.state.themes || []).find(t => !t.deletedAt)?.id;
            return (
              <SearchableSelect
                label={t('questions.theme')}
                name="themeId"
                value={selectedThemeId}
                defaultValue={defaultValue}
                placeholder={t('common.searchTheme')}
                options={(dataSource.state.themes || []).filter(t => !t.deletedAt)}
                getOptionValue={(option) => option.id}
                getOptionLabel={(option) => option.nom}
                getOptionSearchText={(option) => option.nom?.toLowerCase() || ""}
                required
                loading={!dataSource.state.themes || dataSource.state.themes.length === 0}
                emptyMessage={t('common.noThemesFound')}
                className="max-w-full"
                onValueChange={(value) => {
                  setSelectedThemeId(value);
                }}
              />
            );
          })()}
        </div>
        <div className="space-y-2">
          <Label htmlFor="enonce">{t('questions.statement')}</Label>
          <Textarea
            id="enonce"
            name="enonce"
            defaultValue={editing?.enonce || ''}
            required
            rows={4}
            className="min-h-[100px]"
            placeholder={t('common.enterQuestion')}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="contentLanguage">{t('common.contentLanguage')}</Label>
          <Select name="contentLanguage" disabled={editing?.id} defaultValue={language || 'fr'}>
            <SelectTrigger>
              <SelectValue placeholder={t('users.selectLanguage')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="fr">Français</SelectItem>
              <SelectItem value="en">English</SelectItem>
              <SelectItem value="es">Español</SelectItem>
              <SelectItem value="pt">Português</SelectItem>
            </SelectContent>
          </Select>
        </div>


      </EditorModal>
      <Confirm open={!!confirm.open} title={confirm.title} description={confirm.description} extra={confirmExtra} onConfirm={confirm.onConfirm} onCancel={confirm.onCancel} />
    </Card>
  );
}